import React, { useState, createContext, useContext, useEffect, useMemo } from "react";
import moment from "moment";
import Select from 'react-select';
import "../../../App.css";
import { useAuth } from '../../../contexts/AuthContext';
import { userService, bookingService, talentScheduleService, locationService } from '../../../api/apiService';
import { customStyles } from '../../../helpers/StyleUtils';
import { bookingLocationShort, bookingLocationFull } from '../../../helpers/locationDisplay';
import { FixedSizeList as List } from 'react-window';
import { ClipLoader } from "react-spinners";

// Calendar Context
const CalendarContext = createContext();

const CalendarProvider = ({ children }) => {
  // Calculate the start of the week (Monday)
  const startOfWeek = moment.utc().add(2, "hours").startOf("isoWeek");

  const [dateRange, setDateRange] = useState({
    start: startOfWeek.toDate(),
    end: moment(startOfWeek).add(6, "days").toDate(), // Add 6 days to get to Sunday
  });

  // Mobile state - track if we're in mobile mode
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Current single day for mobile view
  const [currentDay, setCurrentDay] = useState(new Date());

  // Studio filter — lives here so every talent row sees it without prop drilling through
  // the virtualized desktop list. null = show all studios.
  const [filterLocation, setFilterLocation] = useState(null);

  const updateDateRange = (newRange) => {
    setDateRange(newRange);
  };

  const updateCurrentDay = (newDay) => {
    setCurrentDay(newDay);
  };

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <CalendarContext.Provider
      value={{
        dateRange,
        updateDateRange,
        isMobile,
        currentDay,
        updateCurrentDay,
        filterLocation,
        setFilterLocation,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

const useCalendar = () => useContext(CalendarContext);

const DateNavigator = () => {
  const { dateRange, updateDateRange, isMobile, currentDay, updateCurrentDay } = useCalendar();

  const navigateWeeks = (days) => {
    let newStart = moment(dateRange.start).add(days, "days");
    let newEnd = moment(newStart).add(6, "days");
    updateDateRange({ start: newStart.toDate(), end: newEnd.toDate() });
  };

  const navigateDays = (days) => {
    const newDay = moment(currentDay).add(days, "days").toDate();
    updateCurrentDay(newDay);
  };

  const goToToday = () => {
    if (isMobile) {
      updateCurrentDay(new Date());
    } else {
      const startOfWeek = moment().startOf("isoWeek");
      updateDateRange({ 
        start: startOfWeek.toDate(), 
        end: moment(startOfWeek).add(6, "days").toDate() 
      });
    }
  };

  const generateWeekGrid = () => {
    let grid = [];
    let currentDate = new Date(dateRange.start);

    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      grid.push(date);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return grid;
  };

  const weekGrid = generateWeekGrid();

  if (isMobile) {
    // Mobile day navigation
    return (
      <div
        style={{
          position: "sticky",
          top: "5px",
          padding: "10px",
          backgroundColor: "#ffffff",
          zIndex: "4",
          borderBottom: "1px solid #ddd",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}
      >
        {/* Date Display */}
        <div style={{ 
          textAlign: "center",
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "15px",
          color: "#333"
        }}>
          {moment(currentDay).format('dddd, MMM DD')}
        </div>
        
        {/* Navigation Buttons */}
        <div style={{ 
          display: "flex", 
          justifyContent: "center",
          alignItems: "center",
          gap: "15px"
        }}>
          <button 
            className="button" 
            onClick={() => navigateDays(-1)}
            style={{
              padding: "12px 20px",
              fontSize: "16px",
              fontWeight: "600",
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              minWidth: "80px",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
            }}
          >
            ← PREV
          </button>
          
          <button 
            className="button" 
            onClick={goToToday}
            style={{
              padding: "12px 20px",
              fontSize: "16px",
              fontWeight: "600",
              backgroundColor: "#f8f9fa",
              color: "#333",
              border: "2px solid #ddd",
              borderRadius: "8px",
              minWidth: "80px",
              cursor: "pointer"
            }}
          >
            TODAY
          </button>
          
          <button 
            className="button" 
            onClick={() => navigateDays(1)}
            style={{
              padding: "12px 20px",
              fontSize: "16px",
              fontWeight: "600",
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              minWidth: "80px",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
            }}
          >
            NEXT →
          </button>
        </div>
      </div>
    );
  }

  // Desktop week navigation (unchanged)
  return (
    <div
      style={{
        position: "sticky",
        top: "5px",
        padding: "20px 0",
        backgroundColor: "#ffffff",
        zIndex: "4",
      }}
    >
      <div className="tlistcal-week-grid">
        <div style={{ flexBasis: "200px", display: "flex", gap: "5px" }}>
          <button 
            className="button" 
            onClick={() => navigateWeeks(-7)}
            style={{
              padding: "5px 12px",
              fontSize: "12px",
              minWidth: "50px"
            }}
          >
            Prev
          </button>
          <button 
            className="button" 
            onClick={goToToday}
            style={{
              padding: "5px 12px",
              fontSize: "12px",
              minWidth: "50px"
            }}
          >
            Today
          </button>
          <button 
            className="button" 
            onClick={() => navigateWeeks(7)}
            style={{
              padding: "5px 12px",
              fontSize: "12px",
              minWidth: "50px"
            }}
          >
            Next
          </button>
        </div>
        <div style={{width: "70px"}}></div>

        {weekGrid.map((day, index) => (
          <div key={index} style={{textAlign: "center"}} className="tlistcal-day-column">
            {moment(day).format('dddd DD.MM')}
          </div>
        ))}
      </div>
    </div>
  );
};

// Talent Component
const Talent = ({ talent }) => {
  const { dateRange, isMobile, currentDay, filterLocation } = useCalendar();
  const [availabilities, setAvailabilities] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [awaitings, setAwaitings] = useState([]);
  const [secondoptions, setSecondoptions] = useState([]);

  // What the grid draws: the talent's jobs, narrowed to one studio when the filter is set
  const bookings = useMemo(
    () => (filterLocation
      ? allBookings.filter((b) => bookingLocationShort(b) === filterLocation.value)
      : allBookings),
    [allBookings, filterLocation]
  );

  /** Client plus studio short code, e.g. "Tchibo · EB" — the cells are one line high. */
  const bookingCellLabel = (b) => {
    const client = b?.client?.label || b?.client?.name || 'No Client';
    const short = bookingLocationShort(b);
    return short ? `${client} · ${short}` : client;
  };

  /** Full studio name for the cell tooltip, since the cell itself is truncated. */
  const bookingTooltip = (b) => {
    const client = b?.client?.label || b?.client?.name || 'No Client';
    return `${client} — ${bookingLocationFull(b, 'no location')}`;
  };

  // Get the effective date range based on mobile/desktop mode
  const getEffectiveDateRange = () => {
    if (isMobile) {
      // Even in mobile mode, fetch a week's worth of data centered on current day
      // This ensures we get all bookings, but we'll only display the current day
      const startOfWeek = moment(currentDay).startOf('isoWeek');
      const endOfWeek = moment(currentDay).endOf('isoWeek');
      const mobileRange = {
        start: startOfWeek.format('YYYY-MM-DD'),
        end: endOfWeek.format('YYYY-MM-DD')
      };
      // console.log('📅 MOBILE DATE RANGE (week-based):', mobileRange, 'Current day:', moment(currentDay).format('YYYY-MM-DD'));
      return mobileRange;
    } else {
      const desktopRange = {
        start: moment(dateRange.start).format('YYYY-MM-DD'),
        end: moment(dateRange.end).format('YYYY-MM-DD')
      };
      // console.log('📅 DESKTOP DATE RANGE:', desktopRange, 'Date range:', dateRange);
      return desktopRange;
    }
  };

  useEffect(() => {
    const fetchAvailabilityAndVacation = async () => {
      try {
        // console.log(`Fetching availability for talent ID: ${talent.db_id}`);
        
        if (!talent.db_id) {
          console.error('No talent ID available for fetching availability');
          setAvailabilities([]);
          setVacations([]);
          setAwaitings([]);
          return;
        }
        
        const effectiveRange = getEffectiveDateRange();
        
        const response = await talentScheduleService.getTalentScheduleByRange(talent.db_id, effectiveRange.start, effectiveRange.end);
        // console.log('Received range response for talent', talent.db_id, ':', response);
        
        const data = response.data || {};
        const schedule = data.schedule || {};
        
        // Process availabilities, vacations, awaitings, secondoptions - INCLUDING RELATED TALENTS
        const availabilitiesData = Array.isArray(schedule.availabilities) ? schedule.availabilities : [];
        const vacationsData = Array.isArray(schedule.vacations) ? schedule.vacations : [];
        const awaitingsData = Array.isArray(schedule.awaitings) ? schedule.awaitings : [];
        const secondoptionsData = Array.isArray(schedule.secondoptions) ? schedule.secondoptions : [];
        
        // console.log('Combined schedule data from all related talents for', talent.name || talent.Name, ':', {
          // availabilities: availabilitiesData.length,
          // vacations: vacationsData.length,
          // awaitings: awaitingsData.length,
          // secondoptions: secondoptionsData.length
        // });
        
        // Convert to our state format
        const availabilityEntries = availabilitiesData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at)
        }));
        
        const vacationEntries = vacationsData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at)
        }));
        
        const awaitingEntries = awaitingsData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at)
        }));
        
        const secondoptionEntries = secondoptionsData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at)
        }));
        
        // Update state with the final data
        setAvailabilities(availabilityEntries);
        setVacations(vacationEntries);
        setAwaitings(awaitingEntries);
        setSecondoptions(secondoptionEntries);
      } catch (error) {
        console.error("Unexpected error fetching availability:", error);
        setAvailabilities([]);
        setVacations([]);
        setAwaitings([]);
        setSecondoptions([]);
      }
    };

    fetchAvailabilityAndVacation();
  }, [talent.db_id, dateRange.start, dateRange.end, currentDay, isMobile]);

  useEffect(() => {
    const fetchBookingsForTalent = async () => {
      try {
        // Use schedule endpoint to get combined bookings from related talents
        const effectiveRange = getEffectiveDateRange();
        // console.log('🔍 BOOKING FETCH - Talent:', talent.name || talent.Name, 'ID:', talent.db_id);
        // console.log('🔍 BOOKING FETCH - Effective range:', effectiveRange);
        // console.log('🔍 BOOKING FETCH - Is mobile:', isMobile);
        // console.log('🔍 BOOKING FETCH - Current day:', currentDay);
        
        const response = await talentScheduleService.getTalentScheduleByRange(talent.db_id, effectiveRange.start, effectiveRange.end);
        // console.log('🔍 BOOKING FETCH - Raw response:', response);
        
        // Handle different possible response formats
        let bookingsData = [];
        if (response.data?.schedule?.bookings && Array.isArray(response.data.schedule.bookings)) {
          bookingsData = response.data.schedule.bookings;
          // console.log('🔍 BOOKING FETCH - Using combined bookings from schedule (includes related talents):', bookingsData.length);
          // console.log('🔍 BOOKING FETCH - Sample booking:', bookingsData[0]);
        } else {
          // Fallback to individual talent bookings
          // console.log('🔍 BOOKING FETCH - Falling back to individual talent bookings');
          const fallbackResponse = await bookingService.getBookingsByTalentId(talent.db_id);
          // console.log('🔍 BOOKING FETCH - Fallback response:', fallbackResponse);
          if (fallbackResponse.data?.bookings) {
            bookingsData = fallbackResponse.data.bookings;
          } else if (Array.isArray(fallbackResponse.data)) {
            bookingsData = fallbackResponse.data;
          } else if (Array.isArray(fallbackResponse.bookings)) {
            bookingsData = fallbackResponse.bookings;
          }
          // console.log('🔍 BOOKING FETCH - Using individual talent bookings (fallback):', bookingsData.length);
        }
        
        // console.log('🔍 BOOKING FETCH - All bookings before filtering:', bookingsData);
        bookingsData.forEach((booking, index) => {
          // console.log(`🔍 BOOKING ${index}:`, {
            // id: booking.id,
            // start_date: booking.start_date,
            // client: booking.client?.name || booking.client?.label,
            // status: booking.status
          // });
        });
        
        const filteredBookings = bookingsData.filter(
          booking => booking.status !== "deleted"
        );
        // console.log('🔍 BOOKING FETCH - Filtered bookings (non-deleted):', filteredBookings.length);
        setAllBookings(filteredBookings || []);
      } catch (error) {
        console.error("🚨 Error fetching bookings:", error);
        setAllBookings([]);
      }
    };

    fetchBookingsForTalent();
  }, [talent.db_id, dateRange.start, dateRange.end, currentDay, isMobile]);

  // Function to determine the style for a day in the calendar
  const getDayStyle = (day) => {
    const formattedDate = moment(day).format('YYYY-MM-DD');
    
    // Find all matching entries for this date (including dates within ranges)
    const matchingAvail = availabilities.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    const matchingVacation = vacations.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    const matchingAwaiting = awaitings.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    const matchingSecondoption = secondoptions.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    // Find the latest entry across all types
    const allMatches = [];
    
    matchingAvail.forEach(entry => allMatches.push({ type: 'available', entry }));
    matchingVacation.forEach(entry => allMatches.push({ type: 'vacation', entry }));
    
    if (allMatches.length === 0) {
      return { 
        backgroundColor: "white", 
        opacity: 1, 
        width: isMobile ? '100%' : '70px',
        minWidth: isMobile ? '40px' : '70px'
      };
    }
    
    // Sort by addedAt timestamp (most recent first)
    const now = moment();
    const twoDaysAgo = moment().subtract(2, 'days');
    
    allMatches.forEach(match => {
      const originalTimestamp = match.entry.addedAt;
      const entryTime = moment(originalTimestamp);
      
      // If timestamp is in the future, it's a Firebase issue - replace with 2 days ago
      if (entryTime.isAfter(now)) {
        // console.log(`🚨 FIREBASE ISSUE: Future timestamp detected for ${match.type}: ${originalTimestamp} -> Correcting to 2 days ago`);
        match.entry.addedAt = twoDaysAgo.toISOString();
        match.correctedTimestamp = true;
      }
    });
    
    allMatches.sort((a, b) => moment(b.entry.addedAt).valueOf() - moment(a.entry.addedAt).valueOf());
    
    const latestEntry = allMatches[0];
    
    // Apply styles based on the most recently added entry type
    switch (latestEntry.type) {
      case 'available':
        return { 
          backgroundColor: "#2ACEF1", 
          opacity: 1, 
          width: isMobile ? '100%' : '70px',
          minWidth: isMobile ? '100%' : '70px'
        };
      case 'vacation':
        return { 
          backgroundColor: "#F12A5D", 
          opacity: 1, 
          width: isMobile ? '100%' : '70px',
          minWidth: isMobile ? '100%' : '70px'
        };
      case 'awaiting':
        return { 
          backgroundColor: "#FFA500", 
          opacity: 1, 
          width: isMobile ? '40px' : '70px',
          minWidth: isMobile ? '40px' : '70px'
        };
      case 'secondoption':
        return { 
          backgroundColor: "#FF69B4", 
          opacity: 1, 
          width: isMobile ? '40px' : '70px',
          minWidth: isMobile ? '40px' : '70px'
        };
      default:
        return { 
          backgroundColor: "white", 
          opacity: 1, 
          width: isMobile ? '40px' : '70px',
          minWidth: isMobile ? '40px' : '70px'
        };
    }
  };

  const getBorderRadius = (set) => {
    switch (set) {
      case "On Model":
        return "10px";
      case "on Produkt":
        return "0px";
      default:
        return "5px"; // Default border radius
    }
  };

  const getStatus = (status) => {
    switch (status) {
      case "confirmed":
        return "#15a326";
      case "new order":
        return "#ffffff";
      case "requested":
        return "#FF8806";
      case "talentaccepted":
        return "#3de651";
      case "declined request":
        return "#ff3c00";
      case "optioned":
        return "#0060ff";
      case "optionSolved":
        return "#FFFF00";
      case "hold":
        return "#800080";
      case "lano":
        return "#116c11";
      case "confirmedCreditNote":
        return "#0f8f8f";
      case "Booked":
        return "#ADD8E6";
      default:
        return "#606060";
    }
  };

  const isEarlyShift = (booking) => {
    if (!booking || !booking.start_time) return false;
    
    try {
      const [hours, minutes] = booking.start_time.split(":").map(Number);
      return hours < 12 || (hours === 12 && minutes === 0);
    } catch (error) {
      console.error("Error parsing start time:", error);
      return false;
    }
  };

  const generateDisplayGrid = () => {
    if (isMobile) {
      // Mobile: show only current day
      return [currentDay];
    } else {
      // Desktop: show full week
      let grid = [];
      let currentDate = new Date(dateRange.start);

      for (let i = 0; i < 7; i++) {
        const date = new Date(currentDate);
        grid.push(date);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return grid;
    }
  };

  const displayGrid = generateDisplayGrid();

  const mobileStyles = isMobile ? {
    talentInfo: {
      flexBasis: "100%",
      fontWeight: "600",
      margin: "auto",
      padding: "10px",
      fontSize: "14px",
      borderBottom: "1px solid #eee",
      marginBottom: "10px"
    },
    weekGrid: {
      flexDirection: "column",
      gap: "0"
    },
    dayColumn: {
      width: "100%",
      minHeight: "120px",
      marginBottom: "5px"
    },
    dayContent: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      padding: "10px"
    },
    statusBar: {
      flex: "0 0 40px"
    },
    bookingsContainer: {
      flex: "1 1 auto"
    }
  } : {};

  if (isMobile) {
    // Mobile layout: single day view with same structure as desktop day column
    return (
      <div style={{ 
        marginBottom: "15px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#fff"
      }}>
        {/* Talent Info Header */}
        <div style={{
          padding: "12px",
          borderBottom: "1px solid #eee",
          backgroundColor: "#f8f9fa"
        }}>
          <div style={{ fontWeight: "600", fontSize: "16px", marginBottom: "4px" }}>
            {talent.name || talent.Name}
          </div>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "2px" }}>
            {talent.function || talent.funktion} | {talent.set || talent.bereich}
          </div>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "2px" }}>
            {talent.email}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {talent.rate}€/day
          </div>
        </div>

        {displayGrid.map((day, index) => (
          <div key={index} style={{ padding: "0" }}>
            {/* Debug info for mobile */}
            {console.log('Mobile day view - Current day:', day.toDateString())}
            {console.log('Mobile day view - Available bookings:', bookings.length)}
            {console.log('Mobile day view - Bookings for this day:', bookings.filter(booking => 
              new Date(booking.start_date).toDateString() === day.toDateString()
            ))}
            
            {/* Status bar - same as desktop */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'start', 
              border: 'solid 1px #ddd', 
              minHeight: '25px',
              margin: "0"
            }}>
              <div style={getDayStyle(day)}></div>
            </div>

            {/* Early shifts - same structure as desktop */}
            <div className="early-shifts">
              <div style={{ minHeight: "20px", padding: "5px" }}>
                {bookings
                  .filter(
                    (booking) => {
                      const bookingDate = new Date(booking.start_date).toDateString();
                      const dayDate = day.toDateString();
                      // console.log('Filtering booking:', booking.id, 'booking date:', bookingDate, 'day date:', dayDate, 'match:', bookingDate === dayDate);
                      return bookingDate === dayDate && isEarlyShift(booking);
                    }
                  )
                  .map((filteredBooking) => (
                    <div
                      key={filteredBooking.id}
                      className="tlistcal-booking"
                      style={{
                        borderRadius: getBorderRadius(filteredBooking.set),
                        marginBottom: "2px"
                      }}
                    >
                      <div style={{
                        height: 15, 
                        width: "100%", 
                        fontSize: 12, 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "5px",
                        paddingTop: "0px",
                        paddingBottom: "0px"
                      }}>
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }} title={bookingTooltip(filteredBooking)}>
                          {bookingCellLabel(filteredBooking)}
                        </span>
                        <div
                          style={{
                            height: "5px",
                            width: "5px",
                            padding: "7px",
                            backgroundColor: getStatus(filteredBooking.status),
                            borderRadius: "50%",
                            display: "inline-block",
                            float: "right",
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Late shifts - same structure as desktop */}
            <div className="late-shifts" style={{ minHeight: "25px", padding: "5px" }}>
              {bookings
                .filter(
                  (booking) => {
                    const bookingDate = new Date(booking.start_date).toDateString();
                    const dayDate = day.toDateString();
                    // console.log('Late shift filtering booking:', booking.id, 'booking date:', bookingDate, 'day date:', dayDate, 'match:', bookingDate === dayDate);
                    return bookingDate === dayDate && !isEarlyShift(booking);
                  }
                )
                .map((filteredBooking) => (
                  <div
                    key={filteredBooking.id}
                    className="tlistcal-booking"
                    style={{
                      borderRadius: getBorderRadius(filteredBooking.set),
                      marginBottom: "2px"
                    }}
                  >
                    <div style={{ 
                      height: 25, 
                      width: "100%", 
                      fontSize: 10, 
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "5px",
                      paddingTop: "0px",
                      paddingBottom: "0px"
                    }}>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }} title={bookingTooltip(filteredBooking)}>
                        {bookingCellLabel(filteredBooking)}
                      </span>
                      <div
                        style={{
                          height: "7px",
                          width: "7px",
                          padding: "10px",
                          backgroundColor: getStatus(filteredBooking.status),
                          borderRadius: "50%",
                          display: "inline-block",
                          float: "right",
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Desktop layout: unchanged week view
  return (
    <div className="tlistcal-week-grid">
      <div style={{ flexBasis: "400px", fontWeight: "600", margin: "auto" }}>
        {talent.name || talent.Name}{" "}
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
          {talent.function || talent.funktion} | {talent.set || talent.bereich}
        </div>
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
          {talent.email}
        </div>
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
          {talent.phone}
        </div>
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
        {talent.rate}€/day
        </div>
      </div>

      <div style={{width: "70px"}}></div>

      {displayGrid.map((day, index) => (
        <div
          key={index}
          className="tlistcal-item tlistcal-c-column"
        >
          <div style={{ display: 'flex', justifyContent: 'start', border: 'solid 1px', minHeight: '25px' }}>
            <div style={getDayStyle(day)}></div>
          </div>

          <div className="early-shifts">
            <div style={{ minHeight: "20px" }}>
              {bookings
                .filter(
                  (booking) =>
                    new Date(booking.start_date).toDateString() ===
                      day.toDateString() && isEarlyShift(booking)
                )
                .map((filteredBooking) => (
                  <div
                    key={filteredBooking.id}
                    className="tlistcal-booking"
                    style={{
                      borderRadius: getBorderRadius(filteredBooking.set),
                    }}
                  >
                    <div style={{
                      height: 15, width: "100%", fontSize: 12, overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "5px",
                      paddingTop: "0px",
                      paddingBottom: "0px"
                    }}>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }} title={bookingTooltip(filteredBooking)}>
                        {bookingCellLabel(filteredBooking)}
                      </span>
                      <div
                        style={{
                          height: "5px",
                          width: "5px",
                          padding: "7px",
                          backgroundColor: getStatus(filteredBooking.status),
                          borderRadius: "50%",
                          display: "inline-block",
                          float: "right",
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="late-shifts" style={{ minHeight: "25px" }}>
            {bookings
              .filter(
                (booking) =>
                  new Date(booking.start_date).toDateString() ===
                    day.toDateString() && !isEarlyShift(booking)
              )
              .map((filteredBooking) => (
                <div
                  key={filteredBooking.id}
                  className="tlistcal-booking"
                  style={{
                    borderRadius: getBorderRadius(filteredBooking.set),
                  }}
                >
                  <div style={{ 
                    height: 25, 
                    width: "100%", 
                    fontSize: 10, 
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "5px",
                    paddingTop: "0px",
                    paddingBottom: "0px"
                  }}>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }} title={bookingTooltip(filteredBooking)}>
                      {bookingCellLabel(filteredBooking)}
                    </span>
                    <div
                      style={{
                        height: "7px",
                        width: "7px",
                        padding: "10px",
                        backgroundColor: getStatus(filteredBooking.status),
                        borderRadius: "50%",
                        display: "inline-block",
                        float: "right",
                      }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Component
const TalentCalendarMain = () => {
  const { currentUser } = useAuth();
  const { isMobile, filterLocation, setFilterLocation } = useCalendar();
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);

  // Studio list for the filter. Taken from the locations master data rather than from the
  // loaded jobs, because each talent row fetches its own bookings independently.
  useEffect(() => {
    (async () => {
      try {
        const response = await locationService.getAllLocations(1, 1000);
        const list = response?.data?.locations || response?.data || [];
        const options = (Array.isArray(list) ? list : [])
          .map((loc) => (loc?.short ? { value: loc.short, label: loc.short } : null))
          .filter(Boolean);
        setLocationOptions(options);
      } catch (err) {
        console.error('Could not load locations for the filter:', err);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchUserTalents = async () => {
      try {
        if (!currentUser || !currentUser.email) {
          setError('User information is not available');
          setLoading(false);
          return;
        }

        // Get user profile data
        const response = await userService.getProfile();
        const userData = response?.data?.user;
        
        if (!userData) {
          console.error('User profile data not found');
          setError('Failed to load user profile data');
          setLoading(false);
          return;
        }
        
        // console.log('User profile data:', userData);
        
        // Check if talents array exists and has items
        if (userData.talents && Array.isArray(userData.talents) && userData.talents.length > 0) {
          // console.log(`Found ${userData.talents.length} talents for user:`, userData.talents);
          setTalents(userData.talents);
        } else {
          // console.warn('No talents array found in user data:', userData);
          setError('No talent information found in your profile');
        }
      } catch (err) {
        console.error('Error fetching user talents:', err);
        setError('Failed to load talent information');
      } finally {
        setLoading(false);
      }
    };

    fetchUserTalents();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <ClipLoader color="#000000" size={30} loading={true} />
          <p style={{ marginTop: '15px', fontSize: '16px' }}>
            Loading your calendar...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper page">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h3>Error</h3>
          <p>{error}</p>
          <p>Please try refreshing the page or contact support if the problem persists.</p>
        </div>
      </div>
    );
  }

  if (!talents || talents.length === 0) {
    return (
      <div className="content-wrapper page">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h3>No Talents Found</h3>
          <p>We couldn't find any talent profiles associated with your account.</p>
          <p>If you believe this is an error, please contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <DateNavigator />

      {locationOptions.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
          <Select
            value={filterLocation}
            onChange={setFilterLocation}
            options={locationOptions}
            isClearable
            placeholder="Filter by Location"
            styles={customStyles}
          />
          {filterLocation && (
            <span style={{ fontSize: '13px', color: '#666' }}>
              Showing only jobs at {filterLocation.label}
            </span>
          )}
        </div>
      )}

      <div style={{
        marginTop: "30px", 
        height: isMobile ? "auto" : "calc(100vh - 250px)",
        padding: isMobile ? "0 10px" : "0"
      }}>
        {isMobile ? (
          // Mobile: simple scrollable list
          <div style={{ paddingBottom: "20px" }}>
            {talents.map((talent, index) => (
              <Talent key={talent.db_id || index} talent={talent} />
            ))}
          </div>
        ) : (
          // Desktop: virtualized list (unchanged)
          <List
            height={window.innerHeight - 300}
            itemCount={talents.length}
            itemSize={150}
            width="100%"
            style={{ overflowX: 'hidden' }}
            overscanCount={5}
            itemData={talents}
          >
            {({ index, style, data }) => (
              <div style={style} className="talent-list-item">
                <Talent talent={data[index]} />
              </div>
            )}
          </List>
        )}
      </div>
    </div>
  );
};

const TalentCalendarPage = () => {
  return (
    <CalendarProvider>
      <TalentCalendarMain />
    </CalendarProvider>
  );
};

export default TalentCalendarPage; 